import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../hooks/useAuth";
import { useToast } from "../../../hooks/useToast";
import DashboardLayout from "../../../components/feature/DashboardLayout";

interface CarePlan {
  id: string;
  plan_type: string;
  services: string[];
  status: string;
  notes: string;
  created_at: string;
}

interface Appointment {
  id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/client", icon: "ri-dashboard-line" },
  { label: "Appointments", path: "/dashboard/client/appointments", icon: "ri-calendar-event-line" },
  { label: "Messages", path: "/dashboard/client/messages", icon: "ri-chat-3-line" },
  { label: "Settings", path: "/dashboard/client/settings", icon: "ri-settings-3-line" },
];

export default function ClientDashboard() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const { data: plans } = await supabase
        .from("care_plans")
        .select("*")
        .eq("client_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setCarePlan(plans);

      const { data: appts } = await supabase
        .from("appointments")
        .select("*")
        .eq("client_id", user.id)
        .order("appointment_date", { ascending: true })
        .order("start_time", { ascending: true })
        .limit(5);
      setAppointments(appts || []);
      setLoading(false);
    };
    fetchData();

    // Real-time subscriptions
    const plansChannel = supabase
      .channel('careplans-client')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'care_plans',
        filter: `client_id=eq.${user.id}`,
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New care plan added', 'success');
        if (payload.eventType === 'UPDATE') showToast('Care plan updated', 'info');
        if (payload.eventType === 'DELETE') showToast('Care plan removed', 'error');
      })
      .subscribe();

    const apptsChannel = supabase
      .channel('appointments-client-home')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'appointments',
        filter: `client_id=eq.${user.id}`,
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New appointment scheduled', 'success');
        if (payload.eventType === 'UPDATE') showToast('Appointment updated', 'info');
        if (payload.eventType === 'DELETE') showToast('Appointment cancelled', 'error');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(plansChannel);
      supabase.removeChannel(apptsChannel);
    };
  }, [user]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      scheduled: "bg-blue-50 text-blue-700",
      completed: "bg-green-50 text-green-700",
      cancelled: "bg-red-50 text-red-700",
      no_show: "bg-gray-100 text-gray-600",
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || "bg-gray-100 text-gray-600"}`}>
        {status}
      </span>
    );
  };

  return (
    <DashboardLayout navItems={navItems} title="Client Portal" subtitle="Dashboard">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading your dashboard...</span>
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              {
                label: "Active Care Plan",
                value: carePlan ? carePlan.plan_type : "None",
                icon: "ri-file-list-3-line",
                color: "bg-brand-green-50 text-brand-green-600",
              },
              {
                label: "Upcoming Visits",
                value: String(appointments.filter((a) => a.status === "scheduled").length),
                icon: "ri-calendar-event-line",
                color: "bg-blue-50 text-blue-600",
              },
              {
                label: "Total Visits",
                value: String(appointments.length),
                icon: "ri-check-double-line",
                color: "bg-amber-50 text-amber-600",
              },
              {
                label: "Services",
                value: String(carePlan?.services?.length || 0),
                icon: "ri-service-line",
                color: "bg-purple-50 text-purple-600",
              },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-brand-green-200 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${stat.icon} text-lg`} />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{stat.label}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Care Plan Summary */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-gray-900">Current Care Plan</h2>
                <Link
                  to="/dashboard/client/care-plan"
                  className="text-xs font-medium text-brand-green-600 hover:text-brand-green-700 flex items-center gap-1"
                >
                  View Full Plan
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>
              {carePlan ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-green-100 text-brand-green-700">
                      {carePlan.plan_type}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                      carePlan.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}>
                      {carePlan.status}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Included Services</h3>
                    <div className="flex flex-wrap gap-2">
                      {carePlan.services?.map((svc) => (
                        <span key={svc} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-soft-gray text-gray-700">
                          {svc}
                        </span>
                      ))}
                    </div>
                  </div>
                  {carePlan.notes && (
                    <div className="bg-brand-green-50 rounded-lg p-4 border border-brand-green-100">
                      <h3 className="text-xs font-semibold text-brand-green-700 mb-1 flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-information-line" />
                        </div>
                        Care Notes
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{carePlan.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-xl text-gray-400" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">No active care plan yet.</p>
                  <Link
                    to="/care-plans"
                    className="inline-flex items-center gap-1.5 px-5 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-full hover:bg-brand-green-700 transition-all"
                  >
                    Explore Care Plans
                  </Link>
                </div>
              )}
            </div>

            {/* Upcoming Appointments */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-gray-900">Upcoming Visits</h2>
                <Link
                  to="/dashboard/client/appointments"
                  className="text-xs font-medium text-brand-green-600 hover:text-brand-green-700 flex items-center gap-1"
                >
                  All Visits
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>
              {appointments.length > 0 ? (
                <div className="space-y-3">
                  {appointments.map((appt) => (
                    <div key={appt.id} className="flex items-center gap-3 p-3 rounded-xl bg-soft-gray hover:bg-brand-green-50 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-calendar-line text-brand-green-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{formatDate(appt.appointment_date)}</p>
                        <p className="text-xs text-gray-500">{appt.start_time} – {appt.end_time}</p>
                      </div>
                      {statusBadge(appt.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-calendar-line text-lg text-gray-400" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">No upcoming visits scheduled.</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Schedule Visit", icon: "ri-calendar-check-line", path: "/dashboard/client/appointments" },
                { label: "Message Team", icon: "ri-chat-3-line", path: "/dashboard/client/messages" },
                { label: "Update Profile", icon: "ri-user-settings-line", path: "/dashboard/client/settings" },
                { label: "Get Help", icon: "ri-customer-service-line", path: "/contact" },
              ].map((action) => (
                <Link
                  key={action.label}
                  to={action.path}
                  className="flex items-center gap-3 p-4 rounded-xl bg-soft-gray hover:bg-brand-green-50 border border-transparent hover:border-brand-green-200 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${action.icon} text-brand-green-600`} />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{action.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
